'use client';

import { useEffect, useMemo, useRef } from 'react';
import { useLocale } from 'next-intl';
import { usePathname } from '@/i18n/routing';

//* Context
import { withUIContext } from '@/context';

//* Helpers
import { config } from '@/helpers';

//* Components
import Icon from '../Icon';
import Text from '../Text';
import Logo from '../Logo';
import CustomLink from '../CustomLink';
import BurgerMenuIcon from './BurgerIcon';
import LanguageSwitcher from './LanguageSwitcher';

//* Styles
import HeaderStyle from './style';

//* Data
import { globalData } from '@/data/globalData';

const Header = ({ isBurgerMenuOpen, toggleBurgerMenu, winWidth, closeBurgerMenu }) => {
	const lang = useLocale();

	//! Ref
	const headerRef = useRef();

	//! Pathname
	const pathname = usePathname();

	//! Check
	const mobile = useMemo(() => winWidth < 768, [winWidth]);

	useEffect(() => {
		if (isBurgerMenuOpen) {
			closeBurgerMenu();
		}
	}, [pathname]);

	//! Nav Panel
	const navigationPanel = useMemo(() => {
		return Object.values(config.routes).map((item) => {
			return (
				item.path !== '/' && (
					<CustomLink
						key={item.name}
						url={item.path}
						className={`link-navigation transition ${pathname == item.path ? 'active' : ''}`}>
						<Text
							className={'p2 font-montserrat-arm-medium text-primary-color link-navigation-text transition '}
							text={item.name}
						/>
					</CustomLink>
				)
			);
		});
	}, [pathname]);

	useEffect(() => {
		headerRef.current.classList.add('active');
	}, []);

	return (
		<HeaderStyle ref={headerRef}>
			<div className='inner-wrapper-header'>
				<Logo />

				<nav className={`navigation-container ${mobile ? 'burger-container' : ''} ${isBurgerMenuOpen ? 'active' : ''}`}>
					<div className='inner-navigation-link'>{navigationPanel}</div>

					{mobile ? (
						<div className={'burger-info-block'}>
							<div className='social-block line'>
								<div className='social-inner-block'>
									<Text
										className={`h6 font-montserrat-arm-medium hover-primary-color uppercase align-center follow-us`}
										text={'followUs'}
									/>

									<div className='svg-block'>
										<CustomLink
											external
											url={globalData.socials.facebook}>
											<Icon name='facebook' />
										</CustomLink>

										<CustomLink
											external
											url={globalData.socials.instagram}>
											<Icon name='instagram' />
										</CustomLink>
									</div>
								</div>
							</div>

							<div className={`info-block line`}>
								<Text
									className={`h6 font-montserrat-arm-medium hover-primary-color our-contacts align-center uppercase`}
									text={'ourContacts'}
								/>

								<div className={`data-block`}>
									<Text className={`p p2 text-secondary-color address align-center`}>{globalData[lang].address}</Text>

									<Text className={`p p2 text-secondary-color email align-center`}>
										<CustomLink
											external
											content={globalData.email}
											url={`mailto:${globalData.email}`}
											className={`hover-line`}
										/>
									</Text>

									{globalData.phone.map((item, index) => {
										return (
											<CustomLink
												external
												key={index}
												className={`phone`}
												url={`tel:${item.value}`}>
												<div className='phone-icon-wrapper'>
													<Icon
														className={'text-primary-color phone-icon transition'}
														name={'phone'}
													/>
												</div>

												<Text className={`p2 font-montserrat-arm-medium text-primary-color phone-text transition`}>{item.name}</Text>
											</CustomLink>
										);
									})}
								</div>
							</div>
						</div>
					) : null}
				</nav>

				<div className='wrapper-contact-language'>
					{mobile ? null : (
						<div className='wrapper-contact'>
							<CustomLink
								external
								url={`tel:${globalData.phone[0].value}`}
								className={`phone`}>
								<div className='phone-icon-wrapper'>
									<Icon
										className={'text-primary-color phone-icon transition'}
										name={'phone'}
									/>
								</div>

								<Text className={`p2 font-montserrat-arm-medium text-primary-color phone-text transition`}>{globalData.phone[0].name}</Text>
							</CustomLink>
						</div>
					)}

					<LanguageSwitcher />

					<div
						className='wrapper-menu-button'
						onClick={() => {
							toggleBurgerMenu();
						}}>
						<BurgerMenuIcon />
					</div>
				</div>
			</div>
		</HeaderStyle>
	);
};

export default withUIContext(Header, ['isBurgerMenuOpen', 'toggleBurgerMenu', 'closeBurgerMenu', 'winWidth']);
